import {
  DataUser,
  LoginResponse,
  ChangePasswordModel,
  CheckRecoveryDTO,
  RecoveryDTO,
  NotificationDTO,
  RefreshRequestDTO,
} from './../models/login-models';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { environment } from '@env/environment';
import { Credentials } from '../models/login-models';
import { CookieService } from 'ngx-cookie-service';
import { UserAttributionsEnum } from '@app/@core/constants/user-credentials.constants';
import { MultilevelNodes } from 'ng-material-multilevel-menu';
import { CredentialsService } from './credentials.service';
import {
  CERTIFIED_PAYMENT_TRUE,
  COMMERCIAL_EXTERNAL,
  LOCALSTORAGE_HASCERTIFIEDPAYMENT,
} from '../constants/users.constants';
import { CommercialDTO } from '@app/payment-for-certifications/model/payment-model';

export interface LoginContext {
  username: string;
  password: string;
  remember?: boolean;
}

/**
 * Provides a base for authentication workflow.
 * The login/logout methods should be replaced with proper implementation.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private refreshTokenTimeout: any;
  private menuShell = new BehaviorSubject<MultilevelNodes[]>([]);
  public menuShell$ = this.menuShell.asObservable();

  private _releases = new BehaviorSubject<NotificationDTO[]>([]);
  public releases$ = this._releases.asObservable();

  constructor(
    private _http: HttpClient,
    private credentialsService: CredentialsService,
    private cookieService: CookieService
  ) {}

  /**
   * Authenticates the user.
   * @param context The login parameters.
   * @return The user credentials.
   */
  login(context: LoginContext): Observable<LoginResponse> {
    const dataUser: DataUser = { username: context.username, password: context.password };
    return this._http.post<LoginResponse>(environment.URL_BASE_AUTHENTICATION + environment.subUrlLogin, dataUser);
  }

  /**
   * Logs out the user and clear credentials.
   * @return True if the user was logged out successfully.
   */
  logout() {
    return this._http.get<any>(environment.URL_BASE_AUTHENTICATION + environment.subUrlogout);
  }

  acceptConditions() {
    return this._http.post<any>(environment.URL_BASE_AUTHENTICATION + environment.subUrlAcceptConditions, null);
  }

  userInfo() {
    return this._http.post<Credentials>(environment.URL_BASE_AUTHENTICATION + environment.subUrlUserInfo, null);
  }

  refreshToken(refreshRequest: RefreshRequestDTO): Observable<LoginResponse> {
    console.log('call refreshtoken service');
    return this._http.post<LoginResponse>(
      environment.URL_BASE_AUTHENTICATION + environment.subUrlrefreshToken,
      refreshRequest
    );
  }

  changePassword(passwordModel: ChangePasswordModel) {
    return this._http.post<any>(environment.URL_BASE_AUTHENTICATION + environment.subUrlChangePassword, passwordModel);
  }

  checkPasswordRecovery(checkRecovery: CheckRecoveryDTO) {
    return this._http.post<boolean>(
      environment.URL_BASE_AUTHENTICATION + environment.subUrlCheckRecovery,
      checkRecovery
    );
  }

  passwordRecovery(recovery: RecoveryDTO) {
    return this._http.post<boolean>(environment.URL_BASE_AUTHENTICATION + environment.subUrlRecovery, recovery);
  }

  loadReleasesMandatories() {
    return this._http.post<any>(environment.URL_BASE_ADMIN + environment.SubUrlNotifications, null);
  }

  hasUsersWithCertifiedPayment(commercialDto: CommercialDTO) {
    return this._http.post<boolean>(
      environment.URL_BASE_REQUEST + environment.subUrlHasUsersWithPaymentCertified,
      commercialDto
    );
  }

  postIncreaseCounterNotifications(notificationId: string) {
    const params = new HttpParams().set('notificationId', notificationId);
    return this._http.post<any>(environment.URL_BASE_ADMIN + environment.SubUrlNotificationsCounter, null, {
      params: params,
    });
  }

  postPrintJasperNotification(notificationId: string) {
    const params = new HttpParams().set('notificationId', notificationId);
    return this._http.post<any>(environment.URL_BASE_ADMIN + environment.SubUrlNotificationsJasper, null, {
      params: params,
      responseType: 'blob' as 'json',
    });
  }

  // auto refresh token

  startRefreshTokenTimer(data: any) {
    console.log('refreshtoken data: ', data);
    //TEST: only local
    // const JWT_TOKEN = this.cookieService.get('JWT_ACCESS_COOKIE');
    // const jwtToken = JSON.parse(atob(JWT_TOKEN.split('.')[1]));
    // console.log("Token: ", jwtToken);
    // console.log("token exp: ", jwtToken.exp); //este viene en segundos, por eso se hacía *1000
    //end TEST

    const expires = new Date(data?.accessToken.expiresAt).getTime();
    console.log('expiresat login miliseg:', expires);
    console.log('expiresat login', data.accessToken.expiresAt);

    const diftimeout = expires - Date.now(); //- 1000; //(60 * 1000); //esto da 16,666 h
    const timeout = diftimeout - 60000; //restamos 1m (60000 milisegs)
    console.log('timeout: ', timeout);

    if (timeout <= 0) {
      console.log('borrando credenciales');
      this.credentialsService.setCredentials();
      this.stopRefreshTokenTimer();
      //hacer logout
      this.logout();
    } else {
      this.stopRefreshTokenTimer();
      this.refreshTokenTimeout = setTimeout(() => {
        this.sendRefreshToken({ isChangeTab: false });
      }, timeout);
    }
  }

  sendRefreshToken(refreshRequest: RefreshRequestDTO) {
    this.refreshToken(refreshRequest)
      .toPromise()
      .then((responseRefresh) => {
        console.log('refrescando token');
        this.startRefreshTokenTimer(responseRefresh);
        localStorage.setItem('expiredAt', JSON.stringify(responseRefresh));
      });
  }

  stopRefreshTokenTimer() {
    clearTimeout(this.refreshTokenTimeout);
  }

  getUserAttributions(): string[] {
    return this.credentialsService.credentials.attribIds;
  }

  getUserUrlAttributions(): string[] {
    return this.credentialsService.credentials.urls;
  }

  // función para saber si tiene permisos para realizar una acción
  permissionAction(action: UserAttributionsEnum): boolean {
    const attributionsUser: string[] = this.getUserAttributions();
    if (attributionsUser && attributionsUser.indexOf(action) === -1) {
      return false;
    }
    return true;
  }

  // función para saber si puede acceder a esa url
  permissionURL(url: string): boolean {
    let urlUser: string[] = this.getUserUrlAttributions();
    urlUser = urlUser.map(function (el) {
      return el.trim();
    });
    if (urlUser && urlUser.indexOf(url) === -1) {
      return false;
    }
    return true;
  }

  isRepsol() {
    const attributionsUser: string[] = this.getUserAttributions();
    if (attributionsUser && attributionsUser.indexOf(UserAttributionsEnum.ATTR_REDUCED_CAPTAIN) >= 0) {
      return true;
    }
    return false;
  }

  isGS() {
    const attributionsUser: string[] = this.getUserAttributions();
    if (attributionsUser && attributionsUser.indexOf(UserAttributionsEnum.ATTR_REDUCED_CAPTAIN_GS) >= 0) {
      return true;
    }
    return false;
  }

  isCrediCuenta(financialProduct: string): boolean {
    return this.credentialsService.credentials.brand !== null && financialProduct === '30';
  }

  getMenuShell(): Observable<MultilevelNodes[]> {
    return this.menuShell$;
  }

  sendMenuShell(menu: MultilevelNodes[]) {
    this.menuShell.next(menu);
  }

  getReleases(): Observable<NotificationDTO[]> {
    return this.releases$;
  }

  setReleases(release: NotificationDTO[]) {
    this._releases.next(release);
  }

  isPaymentCertifications(): boolean {
    const dataString: string = localStorage.getItem('credentials');
    const userInfo = JSON.parse(dataString);
    if (userInfo?.userType === COMMERCIAL_EXTERNAL) {
      return localStorage.getItem(LOCALSTORAGE_HASCERTIFIEDPAYMENT) === 'S';
    }
    return userInfo?.certificatePurchase !== null && userInfo?.certificatePurchase === CERTIFIED_PAYMENT_TRUE;
  }
}
